import React from "react";
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";

import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AppProvider, useApp } from "@/contexts/AppContext";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { RealtimeSync } from "@/components/RealtimeSync";
import { RouteSuspense } from "@/components/RouteSuspense";
import { FeatureErrorBoundary } from "@/components/FeatureErrorBoundary";
import { trackOnboardingDeferred } from "@/lib/analytics";

// Catch-all + error recovery for proxy-mangled URLs (renders NotFound internally).
import { RouteRecovery, RouteErrorElement } from "@/components/RouteRecovery";
import { supabase, isAuthStoragePersistent } from "@/integrations/supabase/client";
import { LoadingSplash } from "./components/ui/LoadingSplash";
import { AppLayout } from "@/components/AppLayout";
import { CookieConsent } from "@/components/CookieConsent";
import { ActivationTracker } from "@/components/ActivationTracker";
import { QueryPersistenceGate } from "@/components/QueryPersistenceGate";
import { RollbarRoot } from "@/lib/rollbar";
import { OfficeRhythmIntlProvider } from "@/i18n";
import { toast } from "@/hooks/use-toast";

// Pages — lazy imports (app)
const Analytics = React.lazy(() => import("./pages/Analytics"));
const CalendarPage = React.lazy(() => import("./pages/Dashboard").then(m => ({ default: m.CalendarPage })));
const SettingsPage = React.lazy(() => import("./pages/Settings").then(m => ({ default: m.SettingsPage })));
const UserProfile = React.lazy(() => import("./pages/UserProfile").then(m => ({ default: m.UserProfile })));
const CirclesPage = React.lazy(() => import("./pages/Circles").then(m => ({ default: m.CirclesPage })));

// Pages — lazy imports (auth / marketing / legal)
// These are split out of the main bundle so authenticated users never
// download them, and anonymous users only download the one route they visit.
const Landing = React.lazy(() => import("./pages/Landing"));
const Login = React.lazy(() => import("./pages/Login"));
const Register = React.lazy(() => import("./pages/Register"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const VerifyEmailPage = React.lazy(() => import("./pages/VerifyEmailPage"));
const AuthCallbackPage = React.lazy(() => import("./pages/AuthCallbackPage"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const Terms = React.lazy(() => import("./pages/legal/Terms"));
const Privacy = React.lazy(() => import("./pages/legal/Privacy"));
const AcceptableUse = React.lazy(() => import("./pages/legal/AcceptableUse"));

// Helper: wrap a lazy route in a Suspense + ErrorBoundary firewall. This is
// where `ChunkLoadError` (stale-HTML-after-new-deploy) gets silently handled
// via a soft reload, and any other render-time error in a route is rendered
// through the full-screen `GlobalErrorFallback` instead of crashing the shell.
const withSuspense = (node: React.ReactNode) => (
  <RouteSuspense>{node}</RouteSuspense>
);

// ─────────────────────────────────────────────
// PROTECTED ROUTE — PERSISTENT SHELL
//
// Gate 1: redirect to /login if not authenticated.
// Gate 2: show OnboardingWizard (full-screen blocking modal) if not onboarded.
//         The wizard is dismissed only after completeOnboarding() sets
//         profiles.is_onboarded = true and refreshUserData() re-hydrates
//         profile.isOnboarded → showOnboarding becomes false → wizard unmounts.
//
// IMPORTANT — This route now OWNS the <AppLayout> shell (sidebar + header +
// global chrome). Child pages render into <Outlet/> inside that shell, so
// navigating between /dashboard ↔ /circles ↔ /settings only swaps the main
// content area — the sidebar / header / realtime subscriptions / query
// observers that AppLayout sets up no longer unmount-and-remount on every
// route change. This is what makes route transitions feel instant.
// ─────────────────────────────────────────────

function ProtectedRoute() {
  const {
    isAuthenticated,
    isLoadingData,
    showOnboarding,
    deferOnboarding,
  } = useApp();

  // Only block on the very first auth resolution. Once we know the user is
  // authenticated, every subsequent `isLoadingData` transition (e.g.
  // `refreshUserData()` after onboarding) is handled as a background refresh
  // — the shell stays mounted, cached data stays rendered, and the tiny
  // top-bar progress indicator in AppLayout is the only UI signal.
  if (isLoadingData && !isAuthenticated) {
    return <LoadingSplash />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleDeferOnboarding = () => {
    trackOnboardingDeferred();
    deferOnboarding();
  };

  return (
    <AppLayout>
      <Outlet />

      {/* Onboarding wizard — blocking modal until complete or deferred.
          Users can choose "Explore calendar first" to mark days before setup. */}
      <OnboardingWizard isOpen={showOnboarding} onDefer={handleDeferOnboarding} />
    </AppLayout>
  );
}

// ─────────────────────────────────────────────
// ROUTER CONFIG (v6.4+)
// ─────────────────────────────────────────────

const router = createBrowserRouter([
  {
    path: "/",
    errorElement: <RouteErrorElement />,
    children: [
      { index: true, element: withSuspense(<Landing />) },
      { path: "login", element: withSuspense(<Login />) },
      { path: "register", element: withSuspense(<Register />) },
      { path: "verify-email", element: withSuspense(<VerifyEmailPage />) },
      { path: "forgot-password", element: withSuspense(<ForgotPassword />) },
      { path: "reset-password", element: withSuspense(<ResetPassword />) },
      { path: "auth/callback", element: withSuspense(<AuthCallbackPage />) },
      { path: "terms", element: withSuspense(<Terms />) },
      { path: "privacy", element: withSuspense(<Privacy />) },
      { path: "acceptable-use", element: withSuspense(<AcceptableUse />) },

      // ── Protected routes ────────────────────────────────────────────────
      {
        element: <ProtectedRoute />,
        children: [
          { path: "dashboard", element: withSuspense(<CalendarPage />) },
          { path: "analytics", element: withSuspense(<Analytics />) },
          { path: "circles",   element: withSuspense(<CirclesPage />) },
          { path: "settings",  element: withSuspense(<SettingsPage />) },
          { path: "profile",   element: withSuspense(<UserProfile />) },
        ],
      },

      { path: "*", element: <RouteRecovery /> },
    ],
  },
], {
  // React Router v6.4+ accepts a `future` flags object whose upstream type
  // is a strict subset of the runtime-accepted v7-opt-in flags. Casting
  // through `unknown` to `Record<string, boolean>` preserves the shape
  // (string keys, boolean values) without reaching for `any`.
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  } as unknown as Record<string, boolean>,
});

// ─────────────────────────────────────────────
// SERVICE WORKER SYNC
// Componente invisible que envía el token JWT al Service Worker
// para las mutaciones en background.
// ─────────────────────────────────────────────
function ServiceWorkerSync() {
  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const syncWithWorker = (session: Session | null) => {
      if (!session) return;
      navigator.serviceWorker.ready.then(reg => {
        reg.active?.postMessage({
          type:        'INIT_SUPABASE',
          supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
          supabaseKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          accessToken: session.access_token,
        });
      });
    };

    // 1. Obtener la sesión actual al cargar la app
    supabase.auth.getSession().then(({ data }) => syncWithWorker(data.session));

    // 2. Escuchar cambios (login, logout, token refresh automático)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      syncWithWorker(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return null;
}

// ─────────────────────────────────────────────
// AUTH STORAGE NOTICE
// One-time toast when browser storage is blocked (tab-only sessions).
// ─────────────────────────────────────────────
function AuthStorageNotice() {
  React.useEffect(() => {
    if (isAuthStoragePersistent) return;
    toast({
      title: "Sign-in won't persist",
      description:
        "Your browser has storage disabled. You'll stay signed in for this tab only.",
    });
  }, []);

  return null;
}

// ─────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────

const App = () => (
  <RollbarRoot>
    <OfficeRhythmIntlProvider>
      <QueryPersistenceGate>
        <AppProvider>
          <ThemeProvider defaultTheme="system">
            <TooltipProvider>
              <ServiceWorkerSync />
              <AuthStorageNotice />
              <ActivationTracker />
              <Toaster />
              <CookieConsent />
              {/* RealtimeSync mounts Supabase Realtime channels; if a subscription
                throws (e.g. the WebSocket layer dies on an old Safari), we
                scope the failure here so the rest of the app still renders. */}
              <FeatureErrorBoundary
                feature="realtime-sync"
                fallback={() => null}
              >
                <RealtimeSync />
              </FeatureErrorBoundary>
              <RouterProvider router={router} />
            </TooltipProvider>
          </ThemeProvider>
        </AppProvider>
      </QueryPersistenceGate>
    </OfficeRhythmIntlProvider>
  </RollbarRoot>
);

export default App;
